import { useCallback, useEffect, useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { ShieldCheck, FileZip } from "@phosphor-icons/react";
import { downloadAuthFile } from "../../lib/fileOpen";
import HsPackList from "./HsPackList";

/** H&S pack for the client portal (visible items, read-only) and the operative portal (all items + read receipts).
 *  `onReadStatus({loaded,total,read})` lets a contract-visit job page gate "Arrive on site" until everything is read. */
export default function ContractHsPackSection({ contractId, base, title = "Health & safety documents", intro, testid = "hs-pack-section", compact = true, hideWhenEmpty = false, requireRead = false, onReadStatus }) {
  const [data, setData] = useState(null);
  const items = data?.items || data?.hs_pack || null;

  const load = useCallback(() => {
    if (!contractId) return;
    API.get(`${base}/contracts/${contractId}/hs-pack`).then(({ data: d }) => setData(d)).catch(() => setData({ items: [] }));
  }, [contractId, base]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!requireRead || !onReadStatus || !items) return;
    onReadStatus({ loaded: true, total: items.length, read: items.filter(i => i.read_at).length });
  }, [items, requireRead, onReadStatus]);

  const confirmRead = async (it) => {
    try {
      const { data: r } = await API.post(`${base}/contracts/${contractId}/hs-pack/${it.id}/read`);
      setData(d => ({ ...d, hs_pack: (d.hs_pack || []).map(x => (x.id === it.id ? { ...x, read_at: r.read_at } : x)) }));
      toast.success(r.remaining === 0 ? "All documents read — you're good to go" : `Confirmed · ${r.remaining} still to read`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not record that"); }
  };

  if (hideWhenEmpty && items && !items.length) return null;
  const unread = requireRead && items ? items.filter(i => !i.read_at).length : 0;
  return (
    <section className="border border-border rounded-lg bg-card" data-testid={testid}>
      <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-sm font-semibold font-[Chivo] flex items-center gap-1.5"><ShieldCheck size={15} className="text-emerald-700" /> {title}</h2>
        <div className="flex items-center gap-2">
          {items && <span className="text-[11px] text-muted-foreground">{items.length} document{items.length === 1 ? "" : "s"}</span>}
          {items?.length > 0 && <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => downloadAuthFile(`${base}/contracts/${contractId}/hs-pack.zip`, "HS_pack.zip")} data-testid={`${testid}-zip`}><FileZip size={14} className="mr-1" /> Download all</Button>}
        </div>
      </div>
      <div className="p-4">
        {requireRead && items?.length > 0 && (
          <p className={`text-xs rounded-md border px-3 py-2 mb-3 ${unread ? "bg-amber-50 border-amber-200 text-amber-900" : "bg-emerald-50 border-emerald-200 text-emerald-800"}`} data-testid={`${testid}-read-status`}>
            {unread ? <><strong>{unread} document{unread === 1 ? "" : "s"} to read</strong> — open each one and confirm before your first visit on this contract.</> : <>You have read and confirmed every document in this pack.</>}
          </p>
        )}
        {intro && items?.length > 0 && !requireRead && <p className="text-xs text-muted-foreground mb-3">{intro}</p>}
        {items === null ? <p className="text-xs text-muted-foreground">Loading…</p>
          : <HsPackList items={items} compact={compact} pdfUrl={(it) => `${base}/contracts/${contractId}/hs-pack/${it.id}/pdf`} onConfirmRead={requireRead ? confirmRead : undefined}
            emptyText="No health & safety documents have been shared for this contract yet." testid={`${testid}-list`} />}
      </div>
    </section>
  );
}
